<template>
  <form
    ref='formRef'
    :action='submitUrl'
    data-remote='true'
    method='post'
    accept-charset='UTF-8'
    class='lead-form'
    :class='{
      [i18nKey]: true
    }'
    @submit='formSubmit'
  >
    <input type='hidden' name='crm_lead[lead_type]' :value='leadType'>
    <SadIcon v-if='!isSoon' />
    <div class='headline'>{{ tp(`${i18nKey}.headline`) }}</div>
    <div class='subheadline' v-html='tp(`${i18nKey}.subheadline`)' />

    <div class='b-input-v4'>
      <input
        :id='`crm_lead_client_${hash}`'
        ref='clientRef'
        name='crm_lead[client]'
        :placeholder="tp('form.name')"
        type='text'
        autocomplete='off'
      >
      <label :for='`crm_lead_client_${hash}`'>
        {{ tp('form.name') }}
      </label>
    </div>
    <PhoneArea
      ref='phoneArea'
      prefix='crm_lead'
      :graphql-url='graphqlUrl'
      @component:update='form.validate()'
    />

    <PrivacyPolicyConfirmation
      v-if='isPrivacyPolicy'
      ref='privacyPolicy'
      v-model:isPrivacyPolicyAccepted='isPrivacyPolicyAccepted'
      v-model:isPrivacyPolicyError='isPrivacyPolicyError'
      :privacy-url='privacyUrl'
    />

    <input type='submit' :value='tp("form.submit")' disabled>
  </form>
</template>

<script>
export default {
  name: 'StorageLeadForm'
};
</script>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import SadIcon from '@/../images/vue/storage_schema_boxes/lead_form/face-sad.svg?inline';

import ComposableForm from '@/views/composable_form';
import PhoneArea from '../components/phone_area';
import PrivacyPolicyConfirmation from '@/vue/components/privacy_policy_confirmation';

import inputHash from '@/helpers/input_hash';
import track from '@/helpers/track';
import { t, prepared } from '@/helpers/i18n';

const props = defineProps({
  i18nKey: { type: String, required: true },
  submitUrl: { type: String, required: true },
  leadType: { type: String, required: true },
  graphqlUrl: { type: String, required: true },
  privacyUrl: { type: String, required: true }
});
const tp = prepared('frontend.storage_schema_boxes.lead_form');

const isSoon = props.i18nKey === 'is_soon';

const hash = inputHash();

const form = ref(null);
const phoneArea = ref(null);

const formRef = ref(null);
const clientRef = ref(null);

const isPrivacyPolicy = window.IS_BRAND_RU;
const privacyPolicy = ref(null);
const isPrivacyPolicyAccepted = ref(false);
const isPrivacyPolicyError = ref(false);

function formSubmit(e) {
  if (isPrivacyPolicy && !isPrivacyPolicyAccepted.value) {
    isPrivacyPolicyError.value = true;
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}

onMounted(() => {
  form.value = new ComposableForm(formRef.value, {
    fields: [clientRef.value],
    components: [phoneArea.value]
  });

  $(formRef.value).on('ajax:success', async () => {
    clear();

    track({ event: props.trackEvent });

    const { thanksModal } = await import(
      /* webpackChunkName: "modal" */
      '@/views/modal/thanks_modal'
    );
    thanksModal();
  });
});

onBeforeUnmount(() => {
  if (form.value) {
    form.value.destroy();
    form.value = null;
  }
});

const clear = () => {
  clientRef.value.value = '';
  phoneArea.value.clear();
  privacyPolicy.value?.clear();
};
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/button_v4'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'

.lead-form
  align-items: center
  display: flex
  flex-direction: column

  +lte_ipad
    gap: rem4(24px)

  +gte_laptop
    gap: 24px
    margin-left: auto
    margin-right: auto
    max-width: 395px

.headline
  +h4
  letter-spacing: -0.48px
  text-align: center

.subheadline
  +p4
  margin: 0
  max-width: auto
  text-align: center

input[type="submit"]
  +button_v4('primary', 'xl')
  width: 100%
</style>
